import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { postPushMessageConfigPush } from '../../services/api';

export default function PushMessageConfigPage() {
  const [deviceMac, setDeviceMac] = React.useState('');
  const [groupName, setGroupName] = React.useState('');
  const [taskName, setTaskName] = React.useState('');
  const [targetType, setTargetType] = React.useState<'device' | 'group'>('device');
  const [loading, setLoading] = React.useState(false);
  const [snack, setSnack] = React.useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
  const [errors, setErrors] = React.useState<{ target?: string; task?: string }>({});

  const handleSubmit = async () => {
    const macValue = deviceMac.trim();
    const groupValue = groupName.trim();
    const taskValue = taskName.trim();
    const nextErrors: { target?: string; task?: string } = {};
    if (!taskValue) {
      nextErrors.task = 'Task Name is required';
    }
    if (targetType === 'device' && !macValue) {
      nextErrors.target = 'Device MAC is required';
    }
    if (targetType === 'group' && !groupValue) {
      nextErrors.target = 'Group Name is required';
    }
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      setSnack({ open: true, message: 'Complete the required fields', severity: 'error' });
      return;
    }
    setErrors({});
    setLoading(true);
    try {
      await postPushMessageConfigPush({
        mac: targetType === 'device' ? macValue : undefined,
        groupName: targetType === 'group' ? groupValue : undefined,
        taskName: taskValue,
      });
      setSnack({ open: true, message: 'Push message configuration requested', severity: 'success' });
    } catch (err: any) {
      const message = err?.response?.data?.message ?? err?.message ?? 'Request failed';
      setSnack({ open: true, message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };
  return (
    <Box>
      <Typography variant="h5" className="page-title" sx={{ mb: 1 }}>
        Push Message Config
      </Typography>
      <Paper sx={{ p: 2, borderRadius: 2, border: '2px solid rgba(0,0,0,0.06)' }} elevation={2}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Submit a push message configuration for a device or group. Task Name is required, and you must choose exactly one target.
        </Typography>
        <Box sx={{ display: 'grid', gap: 2, maxWidth: 560 }}>
          <FormControl>
            <FormLabel>Target</FormLabel>
            <RadioGroup
              row
              value={targetType}
              onChange={(e) => {
                const value = e.target.value as 'device' | 'group';
                setTargetType(value);
                setErrors((prev) => ({ ...prev, target: undefined }));
                if (value === 'device') {
                  setGroupName('');
                } else {
                  setDeviceMac('');
                }
              }}
            >
              <FormControlLabel value="device" control={<Radio />} label="Device MAC" />
              <FormControlLabel value="group" control={<Radio />} label="Group Name" />
            </RadioGroup>
          </FormControl>
          {targetType === 'device' ? (
            <TextField
              label="Device MAC"
              value={deviceMac}
              onChange={(e) => setDeviceMac(e.target.value)}
              placeholder="AA:BB:CC:DD:EE:FF"
              size="small"
              required
              error={Boolean(errors.target)}
              helperText={errors.target ?? 'Enter a single device MAC address.'}
            />
          ) : (
            <TextField
              label="Group Name"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder="Group name"
              size="small"
              required
              error={Boolean(errors.target)}
              helperText={errors.target ?? 'Enter a single group name.'}
            />
          )}
          <TextField
            label="Task Name"
            value={taskName}
            onChange={(e) => setTaskName(e.target.value)}
            placeholder="Task name"
            size="small"
            required
            error={Boolean(errors.task)}
            helperText={errors.task ?? 'Task Name is mandatory for all requests.'}
          />
          <Button variant="contained" onClick={handleSubmit} disabled={loading}>
            {loading ? 'Submitting...' : 'Submit'}
          </Button>
        </Box>
      </Paper>
      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
